import type { ThemeConfig } from 'antd';

/**
 * BMO design tokens applied to Ant Design. Source of truth: the BMO design guide
 * (`tokens.css` / `components.css`, extracted from Figma). Wired globally via
 * <ConfigProvider theme={antTheme}> so every AntD component inherits BMO styling.
 * See ADR-0015.
 */
const bmo = {
  blue: '#0075be', // primary
  blueDark: '#005587', // hover / active
  ink: '#001928', // primary text
  grey: '#646c76', // secondary text / borders
  red: '#c81414', // error
  greyLight: '#ececee', // disabled bg
  border: '#646c76', // input border
  white: '#ffffff',
} as const;

const fontFamily = '"Heebo", system-ui, -apple-system, BlinkMacSystemFont, sans-serif';

export const antTheme: ThemeConfig = {
  token: {
    colorPrimary: bmo.blue,
    colorInfo: bmo.blue,
    colorPrimaryHover: bmo.blueDark,
    colorPrimaryActive: bmo.blueDark,
    colorError: bmo.red,
    colorTextBase: bmo.ink,
    colorText: bmo.ink,
    colorTextSecondary: bmo.grey,
    // AntD derives the muted greys as alpha ramps off `colorTextBase`, and
    // `<Text type="secondary">` reads colorTextDescription (the 45% step), NOT
    // colorTextSecondary. Left derived, 45% of BMO ink lands at #8c989e — 2.96:1
    // on white, which fails WCAG 1.4.3 (4.5:1) everywhere muted text is used.
    // Pinning both to BMO grey (5.32:1) fixes every `type="secondary"` at the
    // token instead of at 66 call sites. See tasks/a11y-wcag21.md.
    colorTextTertiary: bmo.grey,
    colorTextDescription: bmo.grey,
    colorBorder: bmo.border,
    colorBgContainer: bmo.white,
    colorLink: bmo.blue,
    colorLinkHover: bmo.blueDark,
    fontFamily,
    fontSize: 14, // BMO label/helper size; input values run a touch larger via components
    borderRadius: 4, // BMO input radius
    controlHeight: 48, // BMO input height
  },
  components: {
    // Pill CTAs (BMO: 56px tall, 28px radius, Heebo 14/700) — but keep small
    // buttons (table-row actions) compact so list density isn't blown out.
    Button: {
      controlHeight: 56,
      controlHeightLG: 56,
      controlHeightSM: 36,
      borderRadius: 28,
      borderRadiusLG: 28,
      borderRadiusSM: 18,
      fontWeight: 700,
      contentFontSize: 14,
      contentFontSizeLG: 14,
      contentFontSizeSM: 14,
      paddingInline: 28,
      paddingInlineLG: 32,
      paddingInlineSM: 16,
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Input: {
      controlHeight: 48,
      borderRadius: 4,
      activeBorderColor: bmo.blue,
      hoverBorderColor: bmo.blueDark,
      activeShadow: `0 0 0 1px ${bmo.blue}`,
    },
    InputNumber: {
      controlHeight: 48,
      borderRadius: 4,
      activeBorderColor: bmo.blue,
      hoverBorderColor: bmo.blueDark,
    },
    Select: {
      controlHeight: 48,
      borderRadius: 4,
    },
    DatePicker: {
      controlHeight: 48,
      borderRadius: 4,
      activeBorderColor: bmo.blue,
      hoverBorderColor: bmo.blueDark,
    },
    Radio: {
      // BMO radio: 24px ring, 12px dot (2px border applied in global.css).
      radioSize: 24,
      dotSize: 12,
    },
    Checkbox: {
      borderRadiusSM: 4,
    },
  },
  cssVar: true,
  hashed: true,
};
